// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { BenchmarkSelectorWrapper } from '@advisor-app/components';
import { CorrelationMatrix, LinesChart, Selector } from '@advisor-app/components';
import { AutocompleteInput } from '@shared/ui-kit/components/autocomplete';
import { useDownloadResearchReport } from '@advisor-app/features/my-research';
import { PeriodPicker } from '@shared/ui-kit/components/period-picker';
import { formatDate } from '@shared/ui-kit/utils/format-date';
import { CircularProgress, Fade, Tooltip } from '@mui/material';
import {
  AppIcon,
  IconButton,
  SectionContainer,
  RiskVsReturnsGraph,
  PageContainer,
} from '@shared/ui-kit/components';
import { type FC } from 'react';
import { useComparison } from '../hooks';
import { ComparisonTable } from './comparison-table';
import { useTranslation } from 'react-i18next';

export const Comparison: FC = () => {
  const { t, i18n } = useTranslation();
  const {
    setHist,
    histValue,
    onSelectPortfolio,
    ticker,
    setTicker,
    onSelectTicker,
    benchmark,
    setBenchmark,
    assetsTable,
    correlationMatrix,
    risks,
    backtest,
    onRemoveTicker,
    onDeletePositions,
    portfolioOptions,
    historicalData,
    isLoading,
  } = useComparison();

  const { handleDownloadReport } = useDownloadResearchReport();

  const hasMoreThanSixAssets = assetsTable.length > 6;

  return (
    <PageContainer className="!mx-0 !px-0">
      <div>
        <BenchmarkSelectorWrapper value={benchmark} onChange={setBenchmark} />

        <div className="mt-5 flex flex-row items-start justify-between">
          <div className="flex flex-col">
            <h5
              className="!mt-5 ml-2 font-normal italic text-graph-text"
              data-testid="hist-dates"
            >
              {formatDate(historicalData.start_date, 'long', i18n.language)}
              &nbsp;-&nbsp;
              {formatDate(historicalData.end_date, 'long', i18n.language)}
            </h5>
            <PeriodPicker defaultValue={histValue} onValueChange={setHist} />
          </div>
          <div className="grid grid-cols-2 gap-x-2.5">
            <AutocompleteInput
              error={true}
              name="ticker"
              onChangeValue={e => setTicker(e.target.value.toUpperCase())}
              onSelectValue={
                /* istanbul ignore next */ e => onSelectTicker(e.target.value)
              }
              value={ticker}
              placeholder={t('research.add-asset', { ns: 'research' })}
              leftIcon={<AppIcon icon="Search" color="disabled" size="base" />}
              className="!rounded-full"
            />
            <Selector
              valueKey="id"
              optionKey="name"
              options={portfolioOptions}
              placeholder={t('research.select-list', { ns: 'research' })}
              onSelectValue={onSelectPortfolio}
              groupBy={/* istanbul ignore next */ ({ type }) => type}
            />
          </div>
        </div>
      </div>
      <div className="mt-5 grid gap-4">
        <Fade in={isLoading}>
          <div className="absolute left-0 top-0 z-20 flex h-full w-full items-start justify-center bg-page/50">
            <CircularProgress size={75} className="mt-[25%]" />
          </div>
        </Fade>
        <SectionContainer
          title={t('glossary.positions', { ns: 'glossary' })}
          actions={
            <div className="flex gap-4">
              <Tooltip title={t('common.download-report', { ns: 'common' })}>
                <IconButton
                  icon="Reporting"
                  onClick={() => handleDownloadReport(hasMoreThanSixAssets)}
                  data-testid="comparison-report-button"
                />
              </Tooltip>
              <Tooltip title={t('common.delete-all-positions', { ns: 'common' })}>
                <IconButton
                  icon="Trashcan"
                  onClick={onDeletePositions}
                  data-testid="positions-delete-button"
                  disabled={assetsTable.length === 1}
                />
              </Tooltip>
            </div>
          }
          className="relative overflow-hidden"
        >
          <ComparisonTable
            data={assetsTable}
            onRemoveTicker={onRemoveTicker}
            benchmark={benchmark}
          />
        </SectionContainer>

        <LinesChart
          data={backtest}
          title={t('research.returns-pct', { ns: 'research' })}
          benchmark={benchmark}
        />

        <CorrelationMatrix values={correlationMatrix} />

        <RiskVsReturnsGraph risks={risks} benchmark={benchmark} />
      </div>
    </PageContainer>
  );
};
